import * as gqlQueries from 'lib/graphql/queries';
import storefront from 'lib/storefront';
import nanoajax from 'nanoajax';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { i18n } from 'lib/i18n.js';
import { handleize, convertGQLToProductJson, formatPrice } from 'utils/general';

export default {
  components: {
    'vue-image': () => import('../vue-image/vue-image.vue'),
  },
  props: ['data'],
  i18n,
  data() {
    const gwpRawData = JSON.parse(this.data);
    const thresholds = [];

    for (let i = 1; gwpRawData[`threshold_code_${i}`]; i++) {
      const thresholdCode = gwpRawData[`threshold_code_${i}`];
      const thresholdValue = +gwpRawData[`threshold_value_${i}`];
      const thresholdCollection = gwpRawData[`threshold_collection_${i}`];
      const thresholdQuantity = +gwpRawData[`threshold_quantity_${i}`];

      if (
        thresholdCode &&
        thresholdValue &&
        thresholdCollection &&
        thresholdQuantity
      ) {
        thresholds.push({
          code: thresholdCode,
          value: Math.ceil(thresholdValue * window.Shopify.currency.rate),
          collection: thresholdCollection,
          quantity: thresholdQuantity,
        });
      }

      delete gwpRawData[`threshold_code_${i}`];
      delete gwpRawData[`threshold_value_${i}`];
      delete gwpRawData[`threshold_collection_${i}`];
      delete gwpRawData[`threshold_quantity_${i}`];
    }

    thresholds.sort((a, b) => a.value - b.value);

    return {
      gwp: {
        ...gwpRawData,
        thresholds,
      },
      thresholdUpdated: false,
      activeThreshold: null,
      rulesNumber: null,
    };
  },
  async mounted() {
    this.setGWPStatus(this.gwp.active);

    if (this.isCollection) {
      this.callCollectionProducts();
      this.setGWPHandle(this.gwp.gift_collection);
    } else {
      const filteredThresholds = this.gwp.thresholds.filter(
        (threshold) =>
          threshold.collection &&
          threshold.code &&
          threshold.value &&
          threshold.quantity,
      );
      if (!filteredThresholds?.length || !this.ruleReady.some(Boolean)) return;
      await this.getProductsData();
      this.setGWPThresholds(filteredThresholds);
      this.getActiveCollection();
    }
  },
  computed: {
    ...mapGetters('global', [
      'totalCartAmount',
      'totalCartGWPAmount',
      'cartHasGWPProducts',
      'gwpAlreadyAddedToCart',
      'GWPHandle',
      'GWPProducts',
      'GWPApplicableProducts',
      'GWPRewardProducts',
      'rewardProducts',
      'rewardUserList',
      'rewardProductsLimit',
      'rewardIsOnlyOneProduct',
      'rewardUserProductsLeft',
    ]),
    UrlPromoCode() {
      return localStorage.getItem('UrlPromoCode')
    },
    showGWPBanner() {
      if (
        (this.rule === 'threshold' && this.thresholdValidation === false) ||
        (this.gwp?.filter_code_strict_mode !== 'false' &&
          this.gwp.filter_code !== this.UrlPromoCode)
      ) {
        return false;
      }

      if (this.rule === 'threshold' && !this.gwp?.filter_code) {
        const res = this.gwp.thresholds.find(
          (threshold) =>
            threshold.collection &&
            threshold.code &&
            threshold.value &&
            threshold.quantity,
        );
        if (!res) return false;
      }

      if (this.isCollection && !this.GWPRewardProducts.length) {
        return false;
      }

      if (this.gwp?.filter_code && this.UrlPromoCode) {
        return this.gwp.filter_code === this.UrlPromoCode;
      }
      return true;
    },
    rule() {
      return this.gwp.type;
    },
    ruleReady() {
      if (this.isCollection) {
        if (+this.GWPApplicableProducts >= +this.gwp.collection_min_quantity) {
          return [true];
        }
        this.removeAllGWP();
        return [false];
      }

      const rules = this.thresholdDifference.map((thresholdDifference) => {
        return !this.isCollection && thresholdDifference <= 0;
      });

      const updatedRulesNumber = rules.filter((rule) => rule).length;

      if (this.rulesNumber && this.rulesNumber !== updatedRulesNumber) {
        this.removeAllGWP();
        this.clearUserSelection();
      }

      if (rules.some(Boolean)) {
        this.getGWPData();
      } else {
        this.activeThreshold = -1;
        this.removeAllGWP();
        this.clearUserSelection();
      }
      this.rulesNumber = updatedRulesNumber;
      return rules;
    },
    isCollection() {
      return this.rule === 'collection';
    },
    collectionHandle() {
      if (this.gwp.type == 'collection') {
        return this.gwp.collection.length > 0 ? this.gwp.collection : false;
      }
    },
    activeThresholdCollection() {
      if (!this.isCollection) {
        const sortedProducts = this.gwp.thresholds
          ?.filter((item, index) => {
            if (item.value < this.totalCartAmount) return item;
          })
          .sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0))
          .reverse();

        return sortedProducts?.[0].collection;
      }
    },
    collectionLimit() {
      return parseInt(this.gwp.gift_collection_limit);
    },
    singleThreshold() {
      return this.rule === 'threshold' && this.gwp.thresholds.length === 1;
    },
    image() {
      return this.gwp.image.length > 0 ? this.gwp.image : false;
    },
    showImage() {
      return (
        this.image && (this.gwp.thresholds?.length < 2 || this.isCollection)
      );
    },
    title() {
      return this.gwp.title?.length > 0 ? this.gwp.title : false;
    },
    thresholdValidation() {
      if (this.gwp.thresholds[this.gwp.thresholds.length - 1]?.value) {
        return this.rule === 'threshold'
          ? (this.totalCartGWPAmount * 100) /
              this.gwp.thresholds[this.gwp.thresholds.length - 1]?.value
          : false;
      }
      return false;
    },
    thresholdPrice() {
      return this.gwp.thresholds.map((threshold) =>
        formatPrice(threshold.value, true, true),
      );
    },
    thresholdDifference() {
      return this.gwp.thresholds.map(
        (threshold) => threshold.value - this.totalCartGWPAmount,
      );
    },
    thresholdDifferenceFormat() {
      return this.gwp.thresholds.map((_, i) =>
        formatPrice(this.thresholdDifference[i], true, true),
      );
    },
    thresholdReadyMessage() {
      return this.gwp.thresholds.map((threshold) =>
        formatPrice(threshold.value, true, true),
      );
    },
    bannerStyles() {
      return {
        background: this.gwp.background_color || '#ffffff',
        color: this.gwp.text_color || '#C35C66',
      };
    },
    ctaStyles() {
      return {
        background: this.gwp.gwp_button_background || '#000000',
        color: this.gwp.gwp_button_text_color || '#ffffff',
      };
    },
    showCTA() {
      let res = true;
      if (
        this.isCollection ||
        this.rulesReadyQuantity < 1 ||
        (this.rulesReadyQuantity > 0 &&
          this.rewardProductsLimit === this.rewardUserList.length) ||
        (this.rewardIsOnlyOneProduct && this.gwpAlreadyAddedToCart > 0)
      ) {
        res = false;
      }
      return res;
    },
    ctaText() {
      let res = this.$t('cart.gwp.select');

      if (this.rewardUserProductsLeft === 0) {
        res = this.$t('cart.gwp.gift_added');
      } else if (
        this.singleThreshold ||
        (this.isCollection && this.rewardUserProductsLeft > 0)
      ) {
        res = this.$t('cart.gwp.add');
      }
      return res;
    },
    ctaLabel() {
      let res = this.$t('cart.gwp.select');

      if (this.rewardUserProductsLeft === 0) {
        res = this.$t('cart.gwp.gift_added');
      } else if (
        this.singleThreshold ||
        (this.isCollection && this.rewardUserProductsLeft > 0)
      ) {
        res = this.$t('cart.gwp.label');
      }
      return res;
    },
    ruleDescription() {
      let description = '';
      const giftQualified = this.ruleReady?.some(Boolean);
      const isTieredThreshold = !this.isCollection && !this.singleThreshold;
      const giftsAdded =
        this.rewardProducts.length &&
        this.rewardProductsLimit === this.rewardUserList.length;

      if (this.singleThreshold) {
        if (!giftQualified && !this.cartHasGWPProducts) {
          description = this.$t('cart.gwp.single_next_gift', {
            value: `<strong>${
              this.thresholdDifferenceFormat[this.rulesReadyQuantity]
            }</strong>`,
          });
        } else if (giftQualified && giftsAdded) {
          description = this.$t('cart.gwp.gift_added');
        } else {
          description = this.$t('cart.gwp.qualify_gift');
        }
      } else if (isTieredThreshold) {
        if (this.rulesReadyQuantity === this.gwp.thresholds.length) {
          if (giftsAdded) {
            description = this.$t('cart.gwp.gift_added');
          } else {
            description = this.$t('cart.gwp.qualify_gift');
          }
        } else {
          description = this.$t('cart.gwp.next_gift', {
            value: `<strong>${
              this.thresholdDifferenceFormat[this.rulesReadyQuantity]
            }</strong>`,
          });
        }
      }

      if (this.isCollection) {
        if (this.GWPApplicableProducts < this.gwp.collection_min_quantity) {
          description = this.$t('cart.gwp.collection_gwp_message', {
            quantity: this.gwp.collection_min_quantity,
            collection: `<span class="underline gift-with-purchase__details__description-link">${this.gwp.collection_title}. </span>`,
          });
        } else if (this.rewardUserProductsLeft > 0) {
          description = this.$t('cart.gwp.qualify_gift');
        } else {
          description = this.$t('cart.gwp.gift_added');
        }
      }

      return description;
    },
    rulesReadyQuantity() {
      return this.ruleReady.filter(Boolean).length;
    },
    thresholdPercentage() {
      return this.gwp.thresholds.map(
        (threshold) =>
          (threshold.value * 100) /
          this.gwp.thresholds[this.gwp.thresholds.length - 1]?.value,
      );
    },
    showCollectionCTA() {
      if (
        this.isCollection &&
        this.ruleReady.some(Boolean) &&
        this.GWPApplicableProducts >= +this.gwp.collection_min_quantity &&
        this.rewardUserProductsLeft > 0
      ) {
        return true;
      }
      return false;
    },
  },
  methods: {
    ...mapMutations('global', [
      'setGWPStatus',
      'setGWPHandle',
      'setGWPLayout',
      'setOnlyOneProductGWP',
      'setRewardCollection',
      'setGWPCollectionData',
    ]),
    ...mapActions('global', [
      'requestGWPCollection',
      'requestRewardsCollection',
      'setGWPCollectionLimit',
      'setRewardCollectionLimit',
      'setGWPThresholds',
      'removeAllGWP',
      'clearUserSelection',
      'openModal',
    ]),
    ...mapActions('quickView', [
      'setProduct',
      'updateModalTitle',
      'updateCtaText',
    ]),

    selectGWP() {
      if (this.ruleReady.some(Boolean)) {
        this.updateModalTitle(this.$t('cart.gwp.choose'));
        this.updateCtaText(this.$t('cart.gwp.done'));
        this.setGWPLayout(true);
        this.openModal('gwp');
      }
    },
    async callCollectionProducts() {
      this.requestGWPCollection(this.collectionHandle);
      this.requestRewardsCollection(this.gwp.gift_collection);
      this.setRewardCollectionLimit(this.collectionLimit);

      const collectionGWPData = {
        image: this.gwp.image_collection,
        copy: this.$t('cart.gwp.collection_gwp_item_message'),
        button_text: this.$t('cart.gwp.collection_gwp_button_text'),
        source_collection: this.collectionHandle,
        collection_title: this.gwp.collection_title,
        min_quantity: this.gwp.collection_min_quantity,
      };
      this.setGWPCollectionData(collectionGWPData);
    },
    openQuickShopModal(single) {
      this.updateModalTitle(this.$t('quick_view.quick_view_title'));
      this.updateCtaText(this.$t('quick_view.add_to_bag'));
      this.setProduct(single);
      this.setOnlyOneProductGWP(true);
      this.openModal('quick-view');
    },
    activeHandle() {
      if (!this.gwp.thresholds.length) return;

      if (this.gwp.thresholds.length === 1) {
        return this.gwp.thresholds[0].collection;
      }
      const sortedProducts = this.gwp.thresholds
        ?.filter((item) => {
          if (item.value < this.totalCartAmount) return item;
        })
        .sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0))
        .reverse();

      return sortedProducts?.[0].collection;
    },
    async getProductsData() {
      for (const [i, threshold] of this.gwp.thresholds.entries()) {
        const tempProducts = [];
        const handle = threshold.collection;
        const data = await storefront.request(gqlQueries.collection, {
          handle,
          first: 30,
        });

        let response = [];
        if (data.collection) {
          response = data.collection.products.edges.map((p) =>
            convertGQLToProductJson(p.node),
          );
        }
        const promises = [];
        if (response.length > 0) {
          for (const e of response) {
            const product = e;
            const inGWP = product.variants.some((variant) => {
              return !!variant.isGWPVariant;
            });
            if (inGWP && product.available) {
              const handle = handleize(
                e.title.split(window.__GLOBAL__.productDelimiter)[0],
              );
              const url = `${window.Shopify.routes.root}collections/${handle}?view=collection-shade`;
              promises.push(
                new Promise((resolve, reject) => {
                  nanoajax.ajax({ url }, (code, res) => {
                    if (res.length <= 4) {
                      product.shadeCount = parseInt(res, 10);
                    }
                    resolve(res);
                  });
                }),
              );
            }
            tempProducts.push(product);
          }
        }
        await Promise.all(promises);
        this.gwp.thresholds[i].products = tempProducts;
      }
    },
    getActiveCollection(handle) {
      const collectionHandle = handle ? handle : this.activeHandle();
      this.setGWPHandle(collectionHandle);

      const activeCollection =
        this.gwp.thresholds.length > 1
          ? this.gwp.thresholds.find(
              (threshold) => threshold.collection === collectionHandle,
            )
          : this.gwp.thresholds[0];

      const products = activeCollection.products;
      products && this.setRewardCollection(activeCollection.products);

      this.setRewardCollectionLimit(activeCollection.quantity);

      if (this.gwp.thresholds.length > 1) {
        const activeThresholds = this.gwp.thresholds.filter(
          (item) => item.value < this.totalCartAmount,
        );

        const max = activeThresholds.reduce((prev, current) =>
          prev.value > current.value ? prev : current,
        );

        const index = activeThresholds.indexOf(max);

        this.activeThreshold = index;
      }
    },
    async getGWPData() {
      const prevHandle = this.GWPHandle;
      const newHandle = this.activeHandle();

      if (!prevHandle) {
        const filteredThresholds = this.gwp.thresholds.filter(
          (threshold) =>
            threshold.collection &&
            threshold.code &&
            threshold.value &&
            threshold.quantity,
        );
        await this.getProductsData();
        this.setGWPThresholds(filteredThresholds);
        this.getActiveCollection(newHandle);
      } else {
        if (prevHandle && prevHandle !== newHandle) {
          this.thresholdUpdated = true;
        } else {
          this.thresholdUpdated = false;
        }

        // If threshold is changed, get new gift collection
        if (this.thresholdUpdated) {
          this.getActiveCollection(newHandle);
        }

        const allProducts = this.gwp.thresholds
          .map((threshold) => threshold.products)
          .filter((i) => i)
          .flat();

        const oldGwpProducts = this.gwp.thresholds
          .map((threshold) => {
            if (threshold.collection !== newHandle) {
              return threshold.products;
            }
          })
          .filter((i) => i)
          .flat();

        if (this.gwpAlreadyAddedToCart.length > 0 && this.rewardProducts) {
          const GWPProducts = this.gwpAlreadyAddedToCart.map((GWPId) => {
            const product = allProducts?.find(
              (GWPProduct) =>
                GWPProduct.variants?.find(
                  (variant) => +variant.id === +GWPId,
                ) && GWPProduct,
            );
            if (!product) return;

            return {
              id: GWPId,
              tags: product.tags
                ?.map((tag) => {
                  const match = tag.match(/gwp offer\|(\w+)/i);
                  if (match && match.length > 1) {
                    return match[1].toUpperCase();
                  }
                  return false;
                })
                .filter(Boolean),
            };
          });

          if (!this.changePending) {
            let removeProducts = [];
            if (this.isCollection) {
              removeProducts = GWPProducts.map((product) => {
                if (!this.cartHasGWPProducts) {
                  return product.id;
                }
              });
              this.removeAllGWP(removeProducts);
              this.clearUserSelection();
            }
          }
        }
      }
    },
    openGWPCollection() {
      this.openModal('gwp-collection');
    },
  },
};
